import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useParams } from 'react-router-dom';

import Divider from 'antd/es/divider';

import MonitorService from 'services/monitor';

import featureToggles from 'config/featureToggles';
import theme from 'config/themes';
import useInterval from 'hooks/useInterval';
import usePageTitle from 'hooks/usePageTitle';

import MonitorLayout from 'layouts/Monitor.layout';
import { Text } from 'components/Text';
import { useFeatureToggleItem } from 'components/FeatureToggle';

import { formatDurationMins } from 'helpers/date';

import { processState, progressColors } from '../constants';

import StatusBar from '../components/StatusBar';
import EmptyView from '../components/state/EmptyView';
import WaitingInfoView from '../components/state/WaitingInfoView';
import WaitingToStartView from '../components/state/WaitingToStartView';
import CompletedView from '../components/state/CompletedView';
import InProgressView from '../components/state/InProgressView';
import MaintenanceModeView from '../components/state/MaintenanceModeView';

const BTC_INFO_UPDATE_INTERVAL = 60 * 1000;
const BTC_SWITCHING_INTERVAL = 8 * 1000;
const STANDARD_NR_OF_EMPLOYEES = 2;

const mapMonitorStateToView = {
  [processState.EMPTY]: EmptyView,
  [processState.WAITING_CONTAINER]: WaitingInfoView,
  [processState.WAITING]: WaitingToStartView,
  [processState.IN_PROGRESS]: InProgressView,
  [processState.COMPLETED]: CompletedView,
};

const BtcPage = () => {
  const monitorService = useMemo(() => new MonitorService(), []);
  const params = useParams();
  const [activeBtcIndex, setActiveBtcIndex] = useState(0);
  const [btcInfos, setBtcInfos] = useState([{}]);
  const currentVersionRef = useRef(null);

  const btcFeatureToggle = useFeatureToggleItem(featureToggles.BTC_MONITOR);

  const btcVariant = useMemo(() => btcFeatureToggle?.variant || '', [btcFeatureToggle]);

  const getBtcInfos = useCallback(async () => {
    const reqData = {};
    if (/^\d+$/.test(params.doorId)) {
      reqData.door_id = params.doorId;
    } else {
      reqData.hw_id = params.doorId;
    }

    let btcData = await monitorService.getDoorBtc(reqData).catch(err => {
      console.log(err);
    });

    if (!btcData) {
      return;
    }

    btcData = !Array.isArray(btcData) ? [btcData] : btcData;
    if(btcData && btcData.length > 0)
    {
      let newUiVersion = btcData[0].ui_version;

      if (currentVersionRef.current && newUiVersion !== currentVersionRef.current) {
        window.location.reload();
      }

      currentVersionRef.current = newUiVersion;
    }
    // NOTE: the following lines are for testing purposes
    // btcData = Array(2).fill({}).map((_, index) => {
    //   const currentData = { ...btcData[0] };
    //   if (index % 2 === 0) {
    //     currentData.location_dir = 'RIGHT';
    //   } else {
    //     currentData.location_dir = 'LEFT';
    //   }
    //   return currentData;
    // });

    const btcInfos = btcData.map(btcData => {
      // unloading
      const targetTime = formatDurationMins(btcData.tot_minutes_predicted || 0);
      const currentTime = formatDurationMins(btcData.current_minutes || 0);
      const progress = typeof btcData.percent_complete === 'number'
        ? Math.floor(btcData.percent_complete * 100)
        : 0;

      // loading
      const totalTime = btcData.direction === 'LOAD' ? formatDurationMins(btcData.current_minutes || 0) : null;
      const activeTime = formatDurationMins(btcData.active_time_mins || 0);

      const processMethod = (btcData.real_method || btcData.real_method_original || "").toLowerCase();
      const isForklift = processMethod === 'forklift';
      const isConveyor = processMethod === 'conveyor';
      const isMixed = processMethod === 'mixed';

      const isAnomaly = btcData.is_low_accuracy === 1;
      const isLoad = btcData.direction === 'LOAD';
      const nrOfIndividuals = typeof btcData.prediction_people !== 'number'
        ? STANDARD_NR_OF_EMPLOYEES : btcData.prediction_people;
      const hasLeftArrow = btcData.location_dir === 'LEFT';
      const hasRightArrow = btcData.location_dir === 'RIGHT';

      let progressColor, containerCode = '', state, isBreakTime = false;

      if (btcData.door_state === 'WAITING NEXT SHIFT START') {
        state = processState.IN_PROGRESS;
        isBreakTime = true;
        progressColor = "#fff";
      } else if (btcData.door_state === 'Storage') {
        // Map Storage door_state to COMPLETED state
        state = processState.COMPLETED;
        if (isAnomaly) {
          progressColor = '#828C94';
        } else {
          progressColor = progressColors[btcData.color] || progressColors.empty; 
        }
      } else {
        state = btcData.door_state;
        if (isAnomaly) {
          progressColor = '#828C94';
        } else {
        progressColor = progressColors[btcData.color]; 
        }
      }

      if (isLoad && state === processState.IN_PROGRESS) {
        containerCode = btcData.code;
      } else if (state === processState.WAITING_CONTAINER) {
        containerCode = btcVariant === 'v2' ? '' : 'Waiting...';
      } else {
        containerCode = btcData.code;
      }

      const isCalculatingTargetTime = btcData.tot_minutes_predicted === null;
      const isStorageEquipment = btcData.container_code === "Equipment used for Pallets" || btcData.container_code === "Equipment used for Storage";
      const cname = (btcData.customer_name || '').trim().toLowerCase();
      const isStorageCustomer = cname === 'storage' || cname === 'pallets';
      const isStorageDoorState = btcData.door_state === 'Storage';
      const isStorage = isStorageEquipment || isStorageCustomer || isStorageDoorState;

      return {
        variant: btcVariant,
        doorName: btcData.name,
        code: btcData.code,
        isCalculatingTargetTime,
        containerCode,
        state,
        progress,
        progressColor,
        targetTime,
        currentTime,
        isForklift,
        isConveyor,
        isMixed,
        nrOfIndividuals,
        isMaintenance: btcData.maintenance_mode === 1,
        isAnomaly,
        isLoad,
        isBreakTime,
        isStorage,
        warning: btcData.warning_message,
        hasLeftArrow,
        hasRightArrow,
        targetCph: Math.round(btcData.target_active_cph || 0),
        currentCph: Math.round(btcData.instant_active_cph || 0),
        currentCphColor: btcData.instant_active_cph ? progressColors[btcData.instant_cph_color] : progressColors.white,
        cphLevel: btcData.instant_cph_level || 0,
        cphColor: btcData.instant_cph_color,
        totalTime,
        activeTime,
        baselineCbmah: Math.round(btcData.baseline_cbmah || 0),
        currentCbmah: Math.round(btcData.current_cbmah || 0),
        processMethod,
        unloadMethodCategory:btcData.unload_method_category
      };
    });

    setBtcInfos(btcInfos);
  }, [params.doorId, monitorService, btcVariant]);

  const updateActiveBtcIndex = useCallback(() => {
    if (btcInfos.length > 1) {
      setActiveBtcIndex((activeBtcIndex + 1) % btcInfos.length);
    }
  }, [activeBtcIndex, btcInfos]);

  useEffect(() => {
    getBtcInfos();
  }, [getBtcInfos]);

  useInterval(getBtcInfos, BTC_INFO_UPDATE_INTERVAL);
  useInterval(updateActiveBtcIndex, BTC_SWITCHING_INTERVAL);

  const selectedBtcInfo = btcInfos[activeBtcIndex];

  const pageTitle = selectedBtcInfo?.doorName 
    ? `Monitor - ${selectedBtcInfo.doorName}` 
    : 'Monitor';
  const btcHeaderTextColor = theme.btcTextColor || '#F0FDFF';
  usePageTitle(pageTitle);

  return (
    <BtcLayout
      nrOfDoors={btcInfos.length}
      statsIdx={activeBtcIndex}
      isBtcView={true}
      extra={selectedBtcInfo ? (
        <>
          {selectedBtcInfo.containerCode ? (
            <>
              <ContainerCode
                key={activeBtcIndex}
                textColor={btcHeaderTextColor}
                style={{
                  marginTop: '-4px',
                }}
              >
                {selectedBtcInfo.containerCode}
              </ContainerCode>
              <Divider
                orientation="center"
                type="vertical"
                style={{
                  display: 'inline-block',
                  height: '6vh',
                  margin: '0 2.5vh',
                  borderLeft: `1px solid ${btcHeaderTextColor}`,
                }}
              />
            </>
          ) : null}
          <DoorName textColor={btcHeaderTextColor}>
            {selectedBtcInfo.doorName}
          </DoorName>
        </>
      ) : null}
    >
      {btcInfos.map((btcInfo, idx) => {
        let BtcDoorStateView = btcInfo && mapMonitorStateToView[btcInfo.state];
        if (!BtcDoorStateView) return null;

        if (btcInfo.variant === 'v2' && btcInfo.isMaintenance) {
          BtcDoorStateView = MaintenanceModeView;
        }

        const hasLeftArrow = btcInfos.length > 1 && btcInfo.hasLeftArrow;
        const hasRightArrow = btcInfos.length > 1 && btcInfo.hasRightArrow;

        return (
          <CurrentViewWrapper key={idx}>
            <CurrentViewContainer arrowLeft={hasLeftArrow} arrowRight={hasRightArrow}>
              <BtcDoorStateView {...btcInfo} />
            </CurrentViewContainer>
            {btcInfo.warning && (
              <StatusBar
                isMaintenance={btcInfo.isMaintenance}
                isAnomaly={btcInfo.isAnomaly}
                message={btcInfo.warning}
              />
            )}
          </CurrentViewWrapper>
        );
      })}
    </BtcLayout>
  );
};

const BtcLayout = styled(MonitorLayout)`
  overflow: hidden;

  & .ant-layout-header {
    height: 12vh;
    z-index: 100;
  }

  & .ant-divider {
    display: none;
  }

  & .logo {
    margin-left: 2.5vw;
    width: 17vw;
    height: unset;
  }

  & .header-extra {
    align-items: center;
  }

  & main {
    width: ${props => props.nrOfDoors ? `${props.nrOfDoors * 100}%` : '100%'};
    transform: translateX(-${props => `${(props.statsIdx || 0) * Math.floor(100 / props.nrOfDoors)}%`});
    transition: transform 1s;
    background-color: #000;
    display: flex;
    flex-direction: row;
    flex-wrap: no-wrap;
  }
`;

const CurrentViewWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  
  & > :first-child {
    flex: 1;
  }
`;

const CurrentViewContainer = styled.div`
  position: relative;

  ${props => props.arrowLeft && css`
    margin-left: 6vw;
  `}
  
  ${props => props.arrowRight && css`
    margin-right: 6vw;
  `}

  &::before {
    display: ${props => props.arrowLeft ? 'block' : 'none'};
    content: '';
    background: #fff;
    position: absolute;
    left: -4vw;
    width: 4vw;
    height: 50vh;
    top: 50%;
    transform: translateY(-50%);
    clip-path: polygon(100% 0, 60% 50%, 100% 100%, 0 50%);
  }

  &::after {
    display: ${props => props.arrowRight ? 'block' : 'none'};
    content: '';
    background: #fff;
    position: absolute;
    right: -4vw;
    width: 4vw;
    height: 50vh;
    top: 50%;
    transform: translateY(-50%);
    clip-path: polygon(0 0, 100% 50%, 0 100%, 40% 50%);
  }
`;

const DoorName = styled(Text)`
  ${() => css`
    animation: ${fadeIn} 2s forwards;
  `}
  opacity: 0;
  font-size: 4.2vw;
  color: ${props => props.textColor || '#fff'};
  font-style: normal;
  font-weight: 600;
  margin-right: 2vw;
`;

const ContainerCode = styled(Text)`
  ${() => css`
    animation: ${fadeIn} 2s forwards;
  `}
  opacity: 0;
  font-family: 'Roboto Mono', monospace;
  font-style: normal;
  font-weight: bold;
  font-size: 4.2vw;
  color: ${props => props.textColor || '#fff'};
  letter-spacing: 0.28rem;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  } 
  to {
    opacity: 1;
  }
`;

export default BtcPage;